import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ClipboardList,
  Handshake,
  MapPin,
  Search,
  Truck,
  Wrench,
} from "lucide-react";
import { useQuote } from "./QuoteModal";
import { DEFAULT_ABOUT, loadAbout } from "../Libs/aboutData";
import Seo from "../seo/Seo";
import { PAGE_SEO } from "../seo/pages";
import { breadcrumbSchema } from "../seo/schema";

/* -------------------------------------------------------------------------- */
/*  CONTENT                                                                   */
/*  Everything on this page comes from Firestore (site/about). If that read   */
/*  fails or takes too long, DEFAULT_ABOUT from aboutData.js is shown.        */
/* -------------------------------------------------------------------------- */

const DB_TIMEOUT_MS = 6000;

// The manager stores each step's icon as a key. Unknown keys fall back to a pin.
const ICONS = {
  pin: MapPin,
  search: Search,
  clipboard: ClipboardList,
  truck: Truck,
  wrench: Wrench,
  handshake: Handshake,
};

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);

const str = (v, fallback) => (typeof v === "string" && v.trim() ? v : fallback);

// A half-filled or older document can never break the page: anything missing
// or malformed falls back to the default for that field.
function normalize(data) {
  const d = DEFAULT_ABOUT;
  const intro = data?.intro || {};
  const how = data?.how || {};
  const ceo = data?.ceo || {};
  const cta = data?.cta || {};

  const steps = (Array.isArray(how.steps) ? how.steps : []).filter(
    (s) => s?.title?.trim()
  );
  const bio = (Array.isArray(ceo.bio) ? ceo.bio : []).filter(
    (p) => typeof p === "string" && p.trim()
  );

  return {
    intro: {
      title: str(intro.title, d.intro.title),
      text: str(intro.text, d.intro.text),
    },
    how: {
      title: str(how.title, d.how.title),
      text: str(how.text, d.how.text),
      image: str(how.image, d.how.image),
      imageAlt: str(how.imageAlt, d.how.imageAlt),
      steps: steps.length ? steps : d.how.steps,
    },
    ceo: {
      name: str(ceo.name, d.ceo.name),
      role: str(ceo.role, d.ceo.role),
      // An empty photo is valid: the monogram is shown instead.
      photo: typeof ceo.photo === "string" ? ceo.photo : d.ceo.photo,
      bio: bio.length ? bio : d.ceo.bio,
    },
    cta: {
      title: str(cta.title, d.cta.title),
      text: str(cta.text, d.cta.text),
    },
  };
}

// Renders at once with the defaults, then swaps in the live content when the
// database answers. If it errors or times out, nothing changes.
function useAboutContent() {
  const [content, setContent] = useState(() => normalize(DEFAULT_ABOUT));

  useEffect(() => {
    let alive = true;
    withTimeout(loadAbout(), DB_TIMEOUT_MS)
      .then(({ data }) => {
        if (alive) setContent(normalize(data));
      })
      .catch(() => {
        /* keep the default content from aboutData.js */
      });
    return () => {
      alive = false;
    };
  }, []);

  return content;
}

const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "MM";

/* -------------------------------------------------------------------------- */
/*  PAGE                                                                      */
/* -------------------------------------------------------------------------- */

function CeoPhoto({ ceo }) {
  const [failed, setFailed] = useState(false);

  // A new photo URL gets a fresh chance to load.
  useEffect(() => {
    setFailed(false);
  }, [ceo.photo]);

  const showPhoto = ceo.photo && !failed;

  return (
    <div
      className="relative aspect-[4/5] w-full overflow-hidden bg-gradient-to-br from-[#0A3568] to-[#072445]"
      style={{ borderRadius: 16 }}
    >
      {showPhoto ? (
        <img
          src={ceo.photo}
          alt={`${ceo.name}, ${ceo.role} of MajiMaji Hub`}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
          loading="lazy"
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center font-heading text-7xl font-extrabold text-white/90"
          role="img"
          aria-label={`Photo of ${ceo.name} coming soon`}
        >
          {initials(ceo.name)}
        </div>
      )}
    </div>
  );
}

export default function About() {
  const { openQuote } = useQuote();
  const { intro, how, ceo, cta } = useAboutContent();

  return (
    <div className="bg-white text-[#0E1726]">
      <Seo
        {...PAGE_SEO.about}
        path="/about"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ])}
      />

      {/* ---------------------------- intro ---------------------------- */}
      <section className="bg-gradient-to-b from-[#0A3568] to-[#072445] px-5 pb-20 pt-36 text-white sm:px-8 lg:pb-28 lg:pt-44">
        <div className="mx-auto max-w-7xl">
          <h1 className="font-heading max-w-3xl text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            {intro.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/80 sm:text-xl">
            {intro.text}
          </p>
        </div>
      </section>

      {/* ------------------------ how we work ------------------------- */}
      <section className="px-5 py-20 sm:px-8 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
              {how.title}
            </h2>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-slate-600">
              {how.text}
            </p>
            <img
              src={how.image}
              alt={how.imageAlt}
              loading="lazy"
              className="mt-10 aspect-[5/4] w-full object-cover"
              style={{ borderRadius: 16 }}
            />
          </div>

          <ol className="divide-y divide-slate-200 border-y border-slate-200">
            {how.steps.map(({ title, text, icon }, i) => {
              const Icon = ICONS[icon] || MapPin;
              return (
                <li key={title + i} className="flex gap-5 py-7">
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center bg-[#F3F7FB] text-[#1F7BE0]"
                    style={{ borderRadius: 12 }}
                  >
                    <Icon size={22} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#1F7BE0]">
                      Step {i + 1}
                    </p>
                    <h3 className="font-heading mt-0.5 text-xl font-bold">
                      {title}
                    </h3>
                    <p className="mt-2 leading-relaxed text-slate-600">
                      {text}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ----------------------------- CEO ----------------------------- */}
      <section className="bg-[#F3F7FB] px-5 py-20 sm:px-8 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
          <div className="mx-auto w-full max-w-md lg:max-w-none">
            <CeoPhoto ceo={ceo} />
          </div>

          <div>
            <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
              {ceo.name}
            </h2>
            <p className="mt-2 text-lg font-semibold text-[#1F7BE0]">
              {ceo.role}
            </p>
            <div className="mt-6 max-w-xl space-y-4 text-lg leading-relaxed text-slate-600">
              {ceo.bio.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ----------------------------- CTA ----------------------------- */}
      <section className="px-5 py-20 sm:px-8 lg:py-24">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
              {cta.title}
            </h2>
            <p className="mt-3 max-w-xl text-lg text-slate-600">{cta.text}</p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => openQuote()}
              className="group inline-flex items-center gap-2 bg-[#F2A93B] px-7 py-4 font-bold text-[#0B1F3A] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0A3568] hover:text-white hover:shadow-xl"
              style={{ borderRadius: 12 }}
            >
              Get a free quote
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 border border-slate-300 px-7 py-4 font-semibold text-[#0A3568] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#0A3568] hover:bg-[#0A3568] hover:text-white"
              style={{ borderRadius: 12 }}
            >
              <Handshake size={18} />
              Our services
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-[#041A33] px-5 pb-32 pt-8 text-sm text-white/60 sm:px-8 lg:pb-8">
        <div className="mx-auto max-w-7xl">
          &copy; {new Date().getFullYear()} MajiMaji Hub. All
          rights reserved.
        </div>
      </footer>
    </div>
  );
}